import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  ScrollView,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  Dimensions,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import Slider from '@react-native-community/slider';
import { Svg, Circle, Text as SvgText } from 'react-native-svg';
import Footer from '../components/Footer';
import { useTheme } from '../contexts/ThemeContext';

const { width: screenWidth } = Dimensions.get('window');
const isWeb = Platform.OS === 'web';
const isMobile = screenWidth < 768;

const BusinessLoanEMICalculatorScreen = () => {
  const { colors } = useTheme();
  const [loanAmount, setLoanAmount] = useState('1000000');
  const [interestRate, setInterestRate] = useState(11.5);
  const [loanTenure, setLoanTenure] = useState(5);
  const [tenureType, setTenureType] = useState('years');
  const [processingFee, setProcessingFee] = useState(1.5);
  const [processingFeeType, setProcessingFeeType] = useState('percentage');
  const [businessRevenue, setBusinessRevenue] = useState('2000000');
  const [businessType, setBusinessType] = useState('manufacturing');
  const [collateralValue, setCollateralValue] = useState('1500000');
  const [insurancePremium, setInsurancePremium] = useState(0.5);
  const [showFAQ, setShowFAQ] = useState(false);
  const [showAffordability, setShowAffordability] = useState(false);
  const [results, setResults] = useState(null);
  const [displayedRows, setDisplayedRows] = useState(5);

  const tenureOptions = [
    { label: 'Years', value: 'years' },
    { label: 'Months', value: 'months' },
  ];

  const businessTypeOptions = [
    { label: 'Manufacturing', value: 'manufacturing' },
    { label: 'Trading', value: 'trading' },
    { label: 'Services', value: 'services' },
    { label: 'Retail', value: 'retail' },
    { label: 'Agriculture', value: 'agriculture' },
    { label: 'Construction', value: 'construction' },
    { label: 'IT/Software', value: 'it_software' },
    { label: 'Healthcare', value: 'healthcare' },
  ];

  const calculateEMI = () => {
    const principal = parseFloat(loanAmount) || 0;
    const rate = parseFloat(interestRate) || 0;
    const tenure = tenureType === 'years' ? parseFloat(loanTenure) * 12 : parseFloat(loanTenure);
    
    if (principal <= 0 || rate <= 0 || tenure <= 0) {
      setResults(null);
      return;
    }

    const monthlyRate = rate / (12 * 100);
    const emi = (principal * monthlyRate * Math.pow(1 + monthlyRate, tenure)) / 
                (Math.pow(1 + monthlyRate, tenure) - 1);
    
    const totalAmount = emi * tenure;
    const totalInterest = totalAmount - principal;
    
    // Calculate processing fee
    let processingFeeAmount = 0;
    if (processingFeeType === 'percentage') {
      processingFeeAmount = (principal * parseFloat(processingFee)) / 100;
    } else {
      processingFeeAmount = parseFloat(processingFee) || 0;
    }
    
    // Calculate insurance premium
    const insuranceAmount = (principal * parseFloat(insurancePremium)) / 100;
    
    // Total cost including all charges
    const totalCost = totalAmount + processingFeeAmount + insuranceAmount;
    
    // Generate amortization schedule
    const schedule = [];
    let remainingPrincipal = principal;
    
    for (let month = 1; month <= Math.min(tenure, 60); month++) {
      const interestPayment = remainingPrincipal * monthlyRate;
      const principalPayment = emi - interestPayment;
      remainingPrincipal -= principalPayment;
      
      if (month <= 12 || month % 12 === 0 || month === tenure) {
        schedule.push({
          year: Math.ceil(month / 12),
          month: month,
          emi: emi,
          principal: principalPayment,
          interest: interestPayment,
          balance: Math.max(0, remainingPrincipal),
        });
      }
    }

    setResults({
      emi: Math.round(emi),
      totalInterest: Math.round(totalInterest),
      totalAmount: Math.round(totalAmount),
      principal: Math.round(principal),
      processingFee: Math.round(processingFeeAmount),
      insurance: Math.round(insuranceAmount),
      totalCost: Math.round(totalCost),
      tenureMonths: Math.round(tenure),
      schedule: schedule,
    });
  };

  useEffect(() => {
    calculateEMI();
  }, [loanAmount, interestRate, loanTenure, tenureType, processingFee, processingFeeType, insurancePremium]);

  const renderHeader = () => (
    <View style={styles.header}>
      <View style={styles.headerContent}>
        <Text style={styles.headerTitle}>Business Loan EMI Calculator</Text>
        <Text style={styles.headerSubtitle}>
          Calculate your business loan EMI with processing fees, insurance, and detailed cost analysis
        </Text>
      </View>
    </View>
  );

  const renderCalculator = () => (
    <View style={styles.calculatorContainer}>
      <Text style={styles.sectionTitle}>Loan Details</Text>
      
      {/* Loan Amount */}
      <View style={styles.inputGroup}>
        <Text style={styles.inputLabel}>Loan Amount (₹)</Text>
        <TextInput
          style={styles.textInput}
          value={loanAmount}
          onChangeText={setLoanAmount}
          placeholder="Enter loan amount"
          keyboardType="numeric"
        />
        <Slider
          style={styles.slider}
          minimumValue={100000}
          maximumValue={50000000}
          value={parseFloat(loanAmount) || 0}
          onValueChange={(value) => setLoanAmount(Math.round(value).toString())}
          minimumTrackTintColor={colors.primary}
          maximumTrackTintColor={colors.border}
          thumbStyle={styles.sliderThumb}
        />
        <View style={styles.sliderLabels}>
          <Text style={styles.sliderLabel}>₹1L</Text>
          <Text style={styles.sliderLabel}>₹5Cr</Text>
        </View>
      </View>

      {/* Interest Rate */}
      <View style={styles.inputGroup}>
        <Text style={styles.inputLabel}>Interest Rate (% per annum)</Text>
        <TextInput
          style={styles.textInput}
          value={interestRate.toString()}
          onChangeText={(value) => setInterestRate(parseFloat(value) || 0)}
          placeholder="Enter interest rate"
          keyboardType="numeric"
        />
        <Slider
          style={styles.slider}
          minimumValue={8}
          maximumValue={20}
          value={interestRate}
          onValueChange={setInterestRate}
          minimumTrackTintColor={colors.primary}
          maximumTrackTintColor={colors.border}
          thumbStyle={styles.sliderThumb}
        />
        <View style={styles.sliderLabels}>
          <Text style={styles.sliderLabel}>8%</Text>
          <Text style={styles.sliderLabel}>20%</Text>
        </View>
      </View>

      {/* Loan Tenure */}
      <View style={styles.inputGroup}>
        <Text style={styles.inputLabel}>Loan Tenure</Text>
        
        {/* Tenure Type Toggle */}
        <View style={styles.toggleContainer}>
          {tenureOptions.map((option) => (
            <TouchableOpacity
              key={option.value}
              style={[
                styles.toggleButton,
                tenureType === option.value && styles.toggleButtonActive,
              ]}
              onPress={() => setTenureType(option.value)}
            >
              <Text
                style={[
                  styles.toggleButtonText,
                  tenureType === option.value && styles.toggleButtonTextActive,
                ]}
              >
                {option.label}
              </Text>
            </TouchableOpacity>
          ))}
        </View>

        <TextInput
          style={styles.textInput}
          value={loanTenure.toString()}
          onChangeText={(value) => setLoanTenure(parseFloat(value) || 0)}
          placeholder={`Enter tenure in ${tenureType}`}
          keyboardType="numeric"
        />
        <Slider
          style={styles.slider}
          minimumValue={tenureType === 'years' ? 1 : 12}
          maximumValue={tenureType === 'years' ? 30 : 360}
          value={loanTenure}
          onValueChange={setLoanTenure}
          minimumTrackTintColor={colors.primary}
          maximumTrackTintColor={colors.border}
          thumbStyle={styles.sliderThumb}
        />
        <View style={styles.sliderLabels}>
          <Text style={styles.sliderLabel}>
            {tenureType === 'years' ? '1 Year' : '12 Months'}
          </Text>
          <Text style={styles.sliderLabel}>
            {tenureType === 'years' ? '30 Years' : '360 Months'}
          </Text>
        </View>
      </View>

      {/* Processing Fee */}
      <View style={styles.inputGroup}>
        <Text style={styles.inputLabel}>Processing Fee</Text>
        
        <View style={styles.toggleContainer}>
          <TouchableOpacity
            style={[
              styles.toggleButton,
              processingFeeType === 'percentage' && styles.toggleButtonActive,
            ]}
            onPress={() => setProcessingFeeType('percentage')}
          >
            <Text
              style={[
                styles.toggleButtonText,
                processingFeeType === 'percentage' && styles.toggleButtonTextActive,
              ]}
            >
              Percentage
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[
              styles.toggleButton,
              processingFeeType === 'fixed' && styles.toggleButtonActive,
            ]}
            onPress={() => setProcessingFeeType('fixed')}
          >
            <Text
              style={[
                styles.toggleButtonText,
                processingFeeType === 'fixed' && styles.toggleButtonTextActive,
              ]}
            >
              Fixed Amount
            </Text>
          </TouchableOpacity>
        </View>

        <TextInput
          style={styles.textInput}
          value={processingFee.toString()}
          onChangeText={(value) => setProcessingFee(parseFloat(value) || 0)}
          placeholder={processingFeeType === 'percentage' ? 'Enter percentage' : 'Enter amount'}
          keyboardType="numeric"
        />
      </View>

      {/* Insurance Premium */}
      <View style={styles.inputGroup}>
        <Text style={styles.inputLabel}>Insurance Premium (% of loan amount)</Text>
        <TextInput
          style={styles.textInput}
          value={insurancePremium.toString()}
          onChangeText={(value) => setInsurancePremium(parseFloat(value) || 0)}
          placeholder="Enter insurance premium percentage"
          keyboardType="numeric"
        />
        <Slider
          style={styles.slider}
          minimumValue={0}
          maximumValue={2}
          value={insurancePremium}
          onValueChange={setInsurancePremium}
          minimumTrackTintColor={colors.primary}
          maximumTrackTintColor={colors.border}
          thumbStyle={styles.sliderThumb}
        />
        <View style={styles.sliderLabels}>
          <Text style={styles.sliderLabel}>0%</Text>
          <Text style={styles.sliderLabel}>2%</Text>
        </View>
      </View>

      {/* Business Details */}
      <Text style={styles.sectionTitle}>Business Details</Text>
      
      {/* Business Type */}
      <View style={styles.inputGroup}>
        <Text style={styles.inputLabel}>Business Type</Text>
        <View style={styles.businessTypeContainer}>
          {businessTypeOptions.map((option) => (
            <TouchableOpacity
              key={option.value}
              style={[
                styles.businessTypeButton,
                businessType === option.value && styles.businessTypeButtonActive,
              ]}
              onPress={() => setBusinessType(option.value)}
            >
              <Text
                style={[
                  styles.businessTypeText,
                  businessType === option.value && styles.businessTypeTextActive,
                ]}
              >
                {option.label}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>

      {/* Annual Business Revenue */}
      <View style={styles.inputGroup}>
        <Text style={styles.inputLabel}>Annual Business Revenue (₹)</Text>
        <TextInput
          style={styles.textInput}
          value={businessRevenue}
          onChangeText={setBusinessRevenue}
          placeholder="Enter annual revenue"
          keyboardType="numeric"
        />
      </View>

      {/* Collateral Value */}
      <View style={styles.inputGroup}>
        <Text style={styles.inputLabel}>Collateral Value (₹)</Text>
        <TextInput
          style={styles.textInput}
          value={collateralValue}
          onChangeText={setCollateralValue}
          placeholder="Enter collateral value"
          keyboardType="numeric"
        />
      </View>

      {/* Business Affordability Check */}
      <TouchableOpacity
        style={styles.affordabilityHeader}
        onPress={() => setShowAffordability(!showAffordability)}
      >
        <Text style={styles.affordabilityTitle}>Business Loan Affordability Analysis</Text>
        <Ionicons
          name={showAffordability ? 'chevron-up' : 'chevron-down'}
          size={24}
          color={colors.primary}
        />
      </TouchableOpacity>

      {showAffordability && (
        <View style={styles.affordabilityContent}>
          {results && (
            <>
              <View style={styles.affordabilityItem}>
                <Text style={styles.affordabilityLabel}>Loan to Collateral Ratio:</Text>
                <Text style={[
                  styles.affordabilityValue,
                  { color: (parseFloat(loanAmount) / parseFloat(collateralValue)) * 100 <= 80 ? colors.success : colors.warning }
                ]}>
                  {((parseFloat(loanAmount) / parseFloat(collateralValue)) * 100).toFixed(1)}%
                </Text>
              </View>
              
              <View style={styles.affordabilityItem}>
                <Text style={styles.affordabilityLabel}>Annual EMI to Revenue Ratio:</Text>
                <Text style={[
                  styles.affordabilityValue,
                  { color: ((results.emi * 12) / parseFloat(businessRevenue)) * 100 <= 30 ? colors.success : colors.warning }
                ]}>
                  {(((results.emi * 12) / parseFloat(businessRevenue)) * 100).toFixed(1)}%
                </Text>
              </View>
              
              <View style={styles.affordabilityItem}>
                <Text style={styles.affordabilityLabel}>Monthly Cash Flow Impact:</Text>
                <Text style={styles.affordabilityValue}>
                  ₹{results.emi.toLocaleString()}
                </Text>
              </View>
              
              <Text style={styles.affordabilityRecommendation}>
                💡 Recommendation: Keep loan-to-collateral ratio below 80% and annual EMI below 30% of revenue for healthy cash flow.
              </Text>
            </>
          )}
        </View>
      )}
    </View>
  );

  const renderDonutChart = () => {
    if (!results) return null;

    const data = [
      { label: 'Principal', value: results.principal, color: colors.primary },
      { label: 'Interest', value: results.totalInterest, color: colors.warning },
      { label: 'Processing Fee', value: results.processingFee, color: colors.error },
      { label: 'Insurance', value: results.insurance, color: colors.success },
    ];

    const total = data.reduce((sum, item) => sum + item.value, 0);
    const radius = 80;
    const strokeWidth = 20;
    const circumference = 2 * Math.PI * radius;
    
    let cumulativePercentage = 0;

    return (
      <View style={styles.chartContainer}>
        <Text style={styles.chartTitle}>Cost Breakdown</Text>
        
        <View style={styles.donutChart}>
          <Svg width={200} height={200}>
            {data.map((item, index) => {
              const percentage = (item.value / total) * 100;
              const strokeDasharray = `${(percentage / 100) * circumference} ${circumference}`;
              const strokeDashoffset = -((cumulativePercentage / 100) * circumference);
              
              cumulativePercentage += percentage;
              
              return (
                <Circle
                  key={index}
                  cx="100"
                  cy="100"
                  r={radius}
                  stroke={item.color}
                  strokeWidth={strokeWidth}
                  fill="transparent"
                  strokeDasharray={strokeDasharray}
                  strokeDashoffset={strokeDashoffset}
                  strokeLinecap="round"
                />
              );
            })}
            <SvgText
              x="100"
              y="95"
              textAnchor="middle"
              fontSize="16"
              fontWeight="bold"
              fill={colors.text}
            >
              Total Cost
            </SvgText>
            <SvgText
              x="100"
              y="115"
              textAnchor="middle"
              fontSize="14"
              fill={colors.textSecondary}
            >
              ₹{(results.totalCost / 100000).toFixed(1)}L
            </SvgText>
          </Svg>
        </View>

        <View style={styles.legendContainer}>
          {data.map((item, index) => (
            <View key={index} style={styles.legendItem}>
              <View style={[styles.legendColor, { backgroundColor: item.color }]} />
              <Text style={styles.legendText}>
                {item.label}: ₹{(item.value / 100000).toFixed(1)}L
              </Text>
            </View>
          ))}
        </View>
      </View>
    );
  };

  const renderResults = () => {
    if (!results) return null;

    return (
      <View style={styles.resultsContainer}>
        <Text style={styles.resultsTitle}>EMI Calculation Results</Text>
        
        <View style={styles.resultGrid}>
          <View style={styles.resultItem}>
            <Text style={styles.resultLabel}>Monthly EMI</Text>
            <Text style={styles.resultValue}>₹{results.emi.toLocaleString()}</Text>
          </View>
          
          <View style={styles.resultItem}>
            <Text style={styles.resultLabel}>Principal Amount</Text>
            <Text style={styles.resultValue}>₹{results.principal.toLocaleString()}</Text>
          </View>
          
          <View style={styles.resultItem}>
            <Text style={styles.resultLabel}>Total Interest</Text>
            <Text style={styles.resultValue}>₹{results.totalInterest.toLocaleString()}</Text>
          </View>
          
          <View style={styles.resultItem}>
            <Text style={styles.resultLabel}>Processing Fee</Text>
            <Text style={styles.resultValue}>₹{results.processingFee.toLocaleString()}</Text>
          </View>
          
          <View style={styles.resultItem}>
            <Text style={styles.resultLabel}>Insurance Premium</Text>
            <Text style={styles.resultValue}>₹{results.insurance.toLocaleString()}</Text>
          </View>
          
          <View style={styles.resultItem}>
            <Text style={styles.resultLabel}>Total Cost</Text>
            <Text style={[styles.resultValue, { color: colors.primary, fontSize: 18, fontWeight: 'bold' }]}>
              ₹{results.totalCost.toLocaleString()}
            </Text>
          </View>
        </View>
      </View>
    );
  };

  const renderAmortizationSchedule = () => {
    if (!results || !results.schedule.length) return null;

    return (
      <View style={styles.scheduleContainer}>
        <Text style={styles.scheduleTitle}>Amortization Schedule</Text>
        <Text style={styles.scheduleSubtitle}>
          Year-wise breakdown of your business loan repayment (showing key milestones)
        </Text>
        
        <View style={styles.tableContainer}>
          <View style={styles.tableHeader}>
            <Text style={[styles.tableHeaderText, { flex: 1 }]}>Year</Text>
            <Text style={[styles.tableHeaderText, { flex: 1.5 }]}>EMI</Text>
            <Text style={[styles.tableHeaderText, { flex: 1.5 }]}>Principal</Text>
            <Text style={[styles.tableHeaderText, { flex: 1.5 }]}>Interest</Text>
            <Text style={[styles.tableHeaderText, { flex: 2 }]}>Balance</Text>
          </View>
          
          {results.schedule.slice(0, displayedRows).map((row, index) => (
            <View key={index} style={styles.tableRow}>
              <Text style={[styles.tableCellText, { flex: 1 }]}>{row.year}</Text>
              <Text style={[styles.tableCellText, { flex: 1.5 }]}>₹{Math.round(row.emi).toLocaleString()}</Text>
              <Text style={[styles.tableCellText, { flex: 1.5 }]}>₹{Math.round(row.principal).toLocaleString()}</Text>
              <Text style={[styles.tableCellText, { flex: 1.5 }]}>₹{Math.round(row.interest).toLocaleString()}</Text>
              <Text style={[styles.tableCellText, { flex: 2 }]}>₹{Math.round(row.balance).toLocaleString()}</Text>
            </View>
          ))}
        </View>
        
        {results.schedule.length > displayedRows && (
          <TouchableOpacity
            style={styles.loadMoreButton}
            onPress={() => setDisplayedRows(prev => Math.min(prev + 5, results.schedule.length))}
          >
            <Text style={styles.loadMoreText}>Load More</Text>
          </TouchableOpacity>
        )}
      </View>
    );
  };

  const renderBusinessLoanInformation = () => (
    <View style={styles.infoContainer}>
      <Text style={styles.infoTitle}>Business Loan Information</Text>
      
      <Text style={styles.infoSubtitle}>What is a Business Loan?</Text>
      <Text style={styles.infoText}>
        A business loan is a debt financing option that provides capital to businesses for various purposes such as 
        expansion, equipment purchase, working capital, or operational expenses. These loans typically require collateral 
        and have specific eligibility criteria based on business revenue and creditworthiness.
      </Text>
      
      <Text style={styles.infoSubtitle}>Key Features</Text>
      <Text style={styles.infoText}>
        • Loan amounts ranging from ₹1 lakh to ₹50 crores{'\n'}
        • Competitive interest rates starting from 8% per annum{'\n'}
        • Flexible repayment tenure up to 30 years{'\n'}
        • Collateral-based and unsecured options available{'\n'}
        • Processing fees typically 1-3% of loan amount{'\n'}
        • Insurance coverage for loan protection
      </Text>
      
      <Text style={styles.infoSubtitle}>Eligibility Criteria</Text>
      <Text style={styles.infoText}>
        • Business should be operational for at least 2-3 years{'\n'}
        • Minimum annual turnover requirements{'\n'}
        • Good credit score and financial track record{'\n'}
        • Adequate collateral for secured loans{'\n'}
        • Proper business registration and licenses
      </Text>
      
      <Text style={styles.infoSubtitle}>How to Use This Calculator?</Text>
      <Text style={styles.infoText}>
        1. Enter your desired loan amount and business details{'\n'}
        2. Set the interest rate offered by your lender{'\n'}
        3. Choose your preferred loan tenure{'\n'}
        4. Add processing fees and insurance costs{'\n'}
        5. Review the affordability analysis and EMI breakdown{'\n'}
        6. Analyze the amortization schedule for planning
      </Text>
    </View>
  );

  const renderSEOContent = () => (
    <View style={styles.seoContainer}>
      <Text style={styles.seoTitle}>Complete Guide to Business Loan EMI Planning & Optimization</Text>
      
      <View style={styles.seoSection}>
        <Text style={styles.seoSubtitle}>Understanding Business Loan EMI Structure</Text>
        <Text style={styles.seoText}>
          Business loan EMI (Equated Monthly Installment) is a crucial financial commitment that directly impacts your company's cash flow and growth potential. Unlike personal loans, business loans often come with additional considerations such as collateral requirements, business revenue assessment, and industry-specific risk factors. Our advanced calculator helps you understand the complete cost structure including processing fees, insurance premiums, and the impact of different business types on your loan terms.
        </Text>
        
        <Text style={styles.seoText}>
          The EMI structure for business loans typically includes principal repayment, interest charges, processing fees, and mandatory insurance costs. Understanding each component helps you make informed decisions about loan tenure, amount, and timing to optimize your business's financial health.
        </Text>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSubtitle}>Key Calculator Features for Business Success</Text>
        <View style={styles.featureGrid}>
          <View style={styles.featureItem}>
            <Text style={styles.featureTitle}>• Multi-Business Type Analysis</Text>
            <Text style={styles.featureDesc}>Tailored calculations for Manufacturing, Trading, Services, Retail, Agriculture, Construction, IT/Software, and Healthcare sectors</Text>
          </View>
          <View style={styles.featureItem}>
            <Text style={styles.featureTitle}>• Comprehensive Cost Analysis</Text>
            <Text style={styles.featureDesc}>Includes processing fees, insurance premiums, and collateral impact assessment</Text>
          </View>
          <View style={styles.featureItem}>
            <Text style={styles.featureTitle}>• Affordability Assessment</Text>
            <Text style={styles.featureDesc}>Revenue-to-EMI ratio analysis and loan-to-collateral ratio calculations</Text>
          </View>
          <View style={styles.featureItem}>
            <Text style={styles.featureTitle}>• Detailed Amortization</Text>
            <Text style={styles.featureDesc}>Year-wise breakdown showing principal vs interest progression</Text>
          </View>
        </View>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSubtitle}>Business Loan vs Other Financing Options</Text>
        <View style={styles.comparisonTable}>
          <View style={styles.comparisonHeader}>
            <Text style={styles.comparisonHeaderText}>Financing Type</Text>
            <Text style={styles.comparisonHeaderText}>Interest Rate</Text>
            <Text style={styles.comparisonHeaderText}>Collateral</Text>
            <Text style={styles.comparisonHeaderText}>Best For</Text>
          </View>
          <View style={styles.comparisonRow}>
            <Text style={styles.comparisonCell}>Secured Business Loan</Text>
            <Text style={styles.comparisonCell}>8.5% - 15%</Text>
            <Text style={styles.comparisonCell}>Required</Text>
            <Text style={styles.comparisonCell}>Large investments</Text>
          </View>
          <View style={styles.comparisonRow}>
            <Text style={styles.comparisonCell}>Unsecured Business Loan</Text>
            <Text style={styles.comparisonCell}>12% - 24%</Text>
            <Text style={styles.comparisonCell}>Not Required</Text>
            <Text style={styles.comparisonCell}>Working capital</Text>
          </View>
          <View style={styles.comparisonRow}>
            <Text style={styles.comparisonCell}>Business Line of Credit</Text>
            <Text style={styles.comparisonCell}>10% - 20%</Text>
            <Text style={styles.comparisonCell}>Varies</Text>
            <Text style={styles.comparisonCell}>Flexible funding</Text>
          </View>
          <View style={styles.comparisonRow}>
            <Text style={styles.comparisonCell}>Equipment Financing</Text>
            <Text style={styles.comparisonCell}>7% - 18%</Text>
            <Text style={styles.comparisonCell}>Equipment</Text>
            <Text style={styles.comparisonCell}>Asset purchase</Text>
          </View>
        </View>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSubtitle}>Step-by-Step Business Loan EMI Calculation</Text>
        <View style={styles.stepsList}>
          <View style={styles.stepItem}>
            <Text style={styles.stepNumber}>1</Text>
            <View style={styles.stepContent}>
              <Text style={styles.stepTitle}>Determine Loan Requirements</Text>
              <Text style={styles.stepDesc}>Assess your business funding needs, considering working capital, expansion plans, equipment purchase, or debt consolidation requirements.</Text>
            </View>
          </View>
          <View style={styles.stepItem}>
            <Text style={styles.stepNumber}>2</Text>
            <View style={styles.stepContent}>
              <Text style={styles.stepTitle}>Evaluate Business Financials</Text>
              <Text style={styles.stepDesc}>Analyze your business revenue, profit margins, cash flow patterns, and existing debt obligations to determine borrowing capacity.</Text>
            </View>
          </View>
          <View style={styles.stepItem}>
            <Text style={styles.stepNumber}>3</Text>
            <View style={styles.stepContent}>
              <Text style={styles.stepTitle}>Compare Interest Rates</Text>
              <Text style={styles.stepDesc}>Research rates from multiple lenders, considering your business type, credit score, and collateral availability.</Text>
            </View>
          </View>
          <View style={styles.stepItem}>
            <Text style={styles.stepNumber}>4</Text>
            <View style={styles.stepContent}>
              <Text style={styles.stepTitle}>Calculate Total Cost</Text>
              <Text style={styles.stepDesc}>Use our calculator to determine EMI, total interest, processing fees, and insurance costs for different scenarios.</Text>
            </View>
          </View>
          <View style={styles.stepItem}>
            <Text style={styles.stepNumber}>5</Text>
            <View style={styles.stepContent}>
              <Text style={styles.stepTitle}>Optimize Loan Structure</Text>
              <Text style={styles.stepDesc}>Adjust loan amount, tenure, and down payment to find the optimal balance between EMI affordability and total cost.</Text>
            </View>
          </View>
        </View>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSubtitle}>Smart Business Loan Optimization Strategies</Text>
        <View style={styles.strategiesList}>
          <View style={styles.strategyItem}>
            <Text style={styles.strategyTitle}>🎯 Revenue-Based Tenure Selection</Text>
            <Text style={styles.strategyDesc}>Align loan tenure with your business revenue cycles. Seasonal businesses should consider longer tenures for lower EMIs during off-peak periods.</Text>
          </View>
          <View style={styles.strategyItem}>
            <Text style={styles.strategyTitle}>💰 Collateral Optimization</Text>
            <Text style={styles.strategyDesc}>Leverage business assets or property as collateral to secure lower interest rates and better terms.</Text>
          </View>
          <View style={styles.strategyItem}>
            <Text style={styles.strategyTitle}>📊 Cash Flow Management</Text>
            <Text style={styles.strategyDesc}>Maintain EMI-to-revenue ratio below 25% to ensure healthy cash flow for business operations and growth.</Text>
          </View>
          <View style={styles.strategyItem}>
            <Text style={styles.strategyTitle}>🔄 Prepayment Planning</Text>
            <Text style={styles.strategyDesc}>Plan partial prepayments during high-revenue periods to reduce total interest burden and loan tenure.</Text>
          </View>
          <View style={styles.strategyItem}>
            <Text style={styles.strategyTitle}>📈 Business Credit Building</Text>
            <Text style={styles.strategyDesc}>Maintain timely EMI payments to build business credit score for better rates on future financing needs.</Text>
          </View>
        </View>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSubtitle}>Legal Compliance & Documentation</Text>
        <Text style={styles.seoText}>
          Business loan applications require comprehensive documentation including business registration certificates, GST returns, income tax returns, bank statements, financial statements, and collateral documents. Ensure all documents are current and accurately reflect your business's financial position. Compliance with RBI guidelines and lender-specific requirements is crucial for loan approval and favorable terms.
        </Text>
        
        <Text style={styles.seoText}>
          Understanding the legal implications of personal guarantees, collateral pledging, and default consequences helps you make informed decisions about loan structure and risk management.
        </Text>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSubtitle}>Common Business Loan Mistakes to Avoid</Text>
        <View style={styles.mistakesList}>
          <Text style={styles.mistakeItem}>❌ Borrowing more than required, leading to unnecessary interest burden</Text>
          <Text style={styles.mistakeItem}>❌ Ignoring processing fees and insurance costs in total cost calculation</Text>
          <Text style={styles.mistakeItem}>❌ Choosing tenure based only on EMI amount without considering total interest</Text>
          <Text style={styles.mistakeItem}>❌ Not maintaining adequate cash reserves for EMI payments during lean periods</Text>
          <Text style={styles.mistakeItem}>❌ Failing to compare offers from multiple lenders and financial institutions</Text>
          <Text style={styles.mistakeItem}>❌ Not reading loan agreement terms carefully, especially prepayment and default clauses</Text>
          <Text style={styles.mistakeItem}>❌ Overleveraging business assets without considering market volatility risks</Text>
        </View>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSubtitle}>Success Stories & Case Studies</Text>
        <View style={styles.caseStudy}>
          <Text style={styles.caseTitle}>Manufacturing Business Expansion</Text>
          <Text style={styles.caseDesc}>
            A textile manufacturing company used our calculator to optimize a ₹50 lakh business loan for machinery purchase. By choosing a 7-year tenure instead of 5 years, they reduced EMI from ₹85,000 to ₹65,000, improving cash flow for operations while accepting a marginal increase in total interest cost.
          </Text>
        </View>
        
        <View style={styles.caseStudy}>
          <Text style={styles.caseTitle}>Service Sector Working Capital</Text>
          <Text style={styles.caseDesc}>
            An IT services company leveraged our affordability analysis to secure a ₹25 lakh working capital loan with EMI-to-revenue ratio of 18%, ensuring sufficient cash flow for employee salaries and operational expenses during project cycles.
          </Text>
        </View>
      </View>
    </View>
  );

  const renderFAQ = () => (
    <View style={styles.faqContainer}>
      <TouchableOpacity
        style={styles.faqHeader}
        onPress={() => setShowFAQ(!showFAQ)}
      >
        <Text style={styles.faqTitle}>FAQ</Text>
        <Ionicons
          name={showFAQ ? 'chevron-up' : 'chevron-down'}
          size={24}
          color={colors.primary}
        />
      </TouchableOpacity>
      
      {showFAQ && (
        <View style={styles.faqContent}>
          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>What is a business loan EMI?</Text>
            <Text style={styles.faqAnswer}>
              EMI (Equated Monthly Installment) is the fixed amount you pay every month to repay your business loan. 
              It includes both principal and interest components, calculated based on loan amount, interest rate, and tenure.
            </Text>
          </View>
          
          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>How is business loan EMI calculated?</Text>
            <Text style={styles.faqAnswer}>
              Business loan EMI is calculated using the formula: EMI = P × r × (1+r)^n / ((1+r)^n - 1), 
              where P is principal, r is monthly interest rate, and n is tenure in months.
            </Text>
          </View>
          
          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>What factors affect business loan interest rates?</Text>
            <Text style={styles.faqAnswer}>
              Key factors include business credit score, annual turnover, business vintage, collateral value, 
              industry type, and the lender's risk assessment of your business.
            </Text>
          </View>
          
          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>What is the ideal loan-to-collateral ratio?</Text>
            <Text style={styles.faqAnswer}>
              Most lenders prefer a loan-to-collateral ratio of 70-80%. This means your collateral value should be 
              at least 25% higher than the loan amount to ensure adequate security.
            </Text>
          </View>
          
          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>How much EMI can my business afford?</Text>
            <Text style={styles.faqAnswer}>
              Generally, your annual EMI should not exceed 30% of your business's annual revenue. This ensures 
              healthy cash flow and ability to meet other operational expenses.
            </Text>
          </View>
          
          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>What are typical business loan interest rates?</Text>
            <Text style={styles.faqAnswer}>
              Business loan interest rates typically range from 8% to 20% per annum, depending on your business 
              profile, creditworthiness, loan amount, and collateral offered.
            </Text>
          </View>
        </View>
      )}
    </View>
  );

  const styles = StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.background,
    },
    header: {
      backgroundColor: colors.primary,
      paddingVertical: 40,
      paddingHorizontal: 20,
    },
    headerContent: {
      width: '100%',
      alignItems: 'center',
    },
    headerTitle: {
      fontSize: 32,
      fontWeight: 'bold',
      color: 'white',
      marginBottom: 8,
      textAlign: 'center',
    },
    headerSubtitle: {
      fontSize: 16,
      color: 'rgba(255, 255, 255, 0.9)',
      textAlign: 'center',
      lineHeight: 24,
    },
    content: {
      flexDirection: isMobile ? 'column' : 'row',
      width: '100%',
      paddingHorizontal: 20,
    },
    leftPanel: {
      flex: isMobile ? 1 : 0.6,
      marginRight: isMobile ? 0 : 20,
    },
    rightPanel: {
      flex: isMobile ? 1 : 0.4,
      marginTop: isMobile ? 20 : 0,
    },
    fullWidthSection: {
      width: '100%',
      paddingHorizontal: 20,
    },
    calculatorContainer: {
      backgroundColor: colors.cardBackground,
      borderRadius: 12,
      padding: 24,
      marginTop: 20,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    sectionTitle: {
      fontSize: 20,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 20,
      borderBottomWidth: 2,
      borderBottomColor: colors.primary,
      paddingBottom: 8,
    },
    inputGroup: {
      marginBottom: 24,
    },
    inputLabel: {
      fontSize: 16,
      fontWeight: '600',
      color: colors.text,
      marginBottom: 8,
    },
    textInput: {
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 8,
      padding: 12,
      fontSize: 16,
      backgroundColor: colors.surface,
      color: colors.text,
    },
    slider: {
      height: 40,
      marginVertical: 10,
    },
    sliderThumb: {
      backgroundColor: colors.primary,
      width: 20,
      height: 20,
    },
    sliderLabels: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      marginTop: 5,
    },
    sliderLabel: {
      fontSize: 12,
      color: colors.textSecondary,
    },
    toggleContainer: {
      flexDirection: 'row',
      backgroundColor: colors.surface,
      borderRadius: 8,
      padding: 4,
      marginBottom: 12,
    },
    toggleButton: {
      flex: 1,
      paddingVertical: 8,
      paddingHorizontal: 16,
      borderRadius: 6,
      alignItems: 'center',
    },
    toggleButtonActive: {
      backgroundColor: colors.primary,
    },
    toggleButtonText: {
      fontSize: 14,
      fontWeight: '600',
      color: colors.textSecondary,
    },
    toggleButtonTextActive: {
      color: 'white',
    },
    businessTypeContainer: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 8,
    },
    businessTypeButton: {
      paddingVertical: 8,
      paddingHorizontal: 12,
      borderRadius: 6,
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surface,
      marginBottom: 8,
    },
    businessTypeButtonActive: {
      backgroundColor: colors.primary,
      borderColor: colors.primary,
    },
    businessTypeText: {
      fontSize: 14,
      color: colors.text,
    },
    businessTypeTextActive: {
      color: 'white',
    },
    affordabilityHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingVertical: 16,
      borderTopWidth: 1,
      borderTopColor: colors.border,
      marginTop: 20,
    },
    affordabilityTitle: {
      fontSize: 18,
      fontWeight: '600',
      color: colors.text,
    },
    affordabilityContent: {
      paddingTop: 16,
    },
    affordabilityItem: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingVertical: 8,
    },
    affordabilityLabel: {
      fontSize: 14,
      color: colors.textSecondary,
      flex: 1,
    },
    affordabilityValue: {
      fontSize: 16,
      fontWeight: '600',
      color: colors.text,
    },
    affordabilityRecommendation: {
      fontSize: 14,
      color: colors.textSecondary,
      backgroundColor: colors.surface,
      padding: 12,
      borderRadius: 8,
      marginTop: 12,
      lineHeight: 20,
    },
    chartContainer: {
      backgroundColor: colors.cardBackground,
      borderRadius: 12,
      padding: 24,
      marginTop: 20,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
      alignItems: 'center',
    },
    chartTitle: {
      fontSize: 20,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 20,
    },
    donutChart: {
      alignItems: 'center',
      marginBottom: 20,
    },
    legendContainer: {
      width: '100%',
    },
    legendItem: {
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: 8,
    },
    legendColor: {
      width: 12,
      height: 12,
      borderRadius: 6,
      marginRight: 8,
    },
    legendText: {
      fontSize: 14,
      color: colors.text,
    },
    resultsContainer: {
      backgroundColor: colors.cardBackground,
      borderRadius: 12,
      padding: 24,
      marginTop: 20,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    resultsTitle: {
      fontSize: 20,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 20,
    },
    resultGrid: {
      gap: 16,
    },
    resultItem: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingVertical: 8,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    resultLabel: {
      fontSize: 14,
      color: colors.textSecondary,
    },
    resultValue: {
      fontSize: 16,
      fontWeight: '600',
      color: colors.text,
    },
    scheduleContainer: {
      backgroundColor: colors.cardBackground,
      borderRadius: 12,
      padding: 24,
      marginBottom: 20,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    scheduleTitle: {
      fontSize: 24,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 8,
    },
    scheduleSubtitle: {
      fontSize: 14,
      color: colors.textSecondary,
      marginBottom: 20,
      lineHeight: 20,
    },
    tableContainer: {
      borderRadius: 8,
      overflow: 'hidden',
      borderWidth: 1,
      borderColor: colors.border,
    },
    tableHeader: {
      flexDirection: 'row',
      backgroundColor: colors.primary,
      paddingVertical: 12,
      paddingHorizontal: 16,
    },
    tableHeaderText: {
      color: 'white',
      fontWeight: '600',
      fontSize: 14,
      textAlign: 'center',
    },
    tableRow: {
      flexDirection: 'row',
      paddingVertical: 12,
      paddingHorizontal: 16,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
      backgroundColor: colors.surface,
    },
    tableCellText: {
      fontSize: 13,
      color: colors.text,
      textAlign: 'center',
    },
    loadMoreButton: {
      backgroundColor: colors.primary,
      paddingVertical: 12,
      paddingHorizontal: 24,
      borderRadius: 8,
      alignItems: 'center',
      marginTop: 16,
    },
    loadMoreText: {
      color: 'white',
      fontWeight: '600',
      fontSize: 14,
    },
    infoContainer: {
      backgroundColor: colors.cardBackground,
      borderRadius: 12,
      padding: 24,
      marginBottom: 20,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    infoTitle: {
      fontSize: 24,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 16,
    },
    infoSubtitle: {
      fontSize: 18,
      fontWeight: '600',
      color: colors.text,
      marginTop: 20,
      marginBottom: 12,
    },
    infoText: {
      fontSize: 14,
      color: colors.textSecondary,
      lineHeight: 22,
    },
    faqContainer: {
      backgroundColor: colors.cardBackground,
      borderRadius: 12,
      marginBottom: 20,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    faqHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: 24,
    },
    faqTitle: {
      fontSize: 24,
      fontWeight: 'bold',
      color: colors.text,
    },
    faqContent: {
      paddingHorizontal: 24,
      paddingBottom: 24,
    },
    faqItem: {
      marginBottom: 20,
    },
    faqQuestion: {
      fontSize: 16,
      fontWeight: '600',
      color: colors.text,
      marginBottom: 8,
    },
    faqAnswer: {
      fontSize: 14,
      color: colors.textSecondary,
      lineHeight: 20,
    },
    // SEO Content Styles
    seoContainer: {
      backgroundColor: colors.cardBackground,
      borderRadius: 12,
      padding: 24,
      marginBottom: 20,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    seoTitle: {
      fontSize: 28,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 24,
      textAlign: 'center',
      borderBottomWidth: 3,
      borderBottomColor: colors.primary,
      paddingBottom: 12,
    },
    seoSection: {
      marginBottom: 32,
    },
    seoSubtitle: {
      fontSize: 22,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 16,
      borderLeftWidth: 4,
      borderLeftColor: colors.primary,
      paddingLeft: 16,
    },
    seoText: {
      fontSize: 16,
      color: colors.textSecondary,
      lineHeight: 24,
      marginBottom: 16,
      textAlign: 'justify',
    },
    featureGrid: {
      gap: 16,
    },
    featureItem: {
      backgroundColor: colors.surface,
      padding: 16,
      borderRadius: 8,
      borderLeftWidth: 4,
      borderLeftColor: colors.primary,
    },
    featureTitle: {
      fontSize: 16,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 8,
    },
    featureDesc: {
      fontSize: 14,
      color: colors.textSecondary,
      lineHeight: 20,
    },
    comparisonTable: {
      borderRadius: 8,
      overflow: 'hidden',
      borderWidth: 1,
      borderColor: colors.border,
    },
    comparisonHeader: {
      flexDirection: 'row',
      backgroundColor: colors.primary,
      paddingVertical: 12,
      paddingHorizontal: 8,
    },
    comparisonHeaderText: {
      flex: 1,
      color: 'white',
      fontWeight: 'bold',
      fontSize: 14,
      textAlign: 'center',
    },
    comparisonRow: {
      flexDirection: 'row',
      paddingVertical: 12,
      paddingHorizontal: 8,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
      backgroundColor: colors.surface,
    },
    comparisonCell: {
      flex: 1,
      fontSize: 13,
      color: colors.text,
      textAlign: 'center',
    },
    stepsList: {
      gap: 16,
    },
    stepItem: {
      flexDirection: 'row',
      backgroundColor: colors.surface,
      padding: 16,
      borderRadius: 8,
      alignItems: 'flex-start',
    },
    stepNumber: {
      width: 32,
      height: 32,
      borderRadius: 16,
      backgroundColor: colors.primary,
      color: 'white',
      fontSize: 16,
      fontWeight: 'bold',
      textAlign: 'center',
      lineHeight: 32,
      marginRight: 16,
    },
    stepContent: {
      flex: 1,
    },
    stepTitle: {
      fontSize: 16,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 8,
    },
    stepDesc: {
      fontSize: 14,
      color: colors.textSecondary,
      lineHeight: 20,
    },
    strategiesList: {
      gap: 16,
    },
    strategyItem: {
      backgroundColor: colors.surface,
      padding: 16,
      borderRadius: 8,
      borderLeftWidth: 4,
      borderLeftColor: colors.success,
    },
    strategyTitle: {
      fontSize: 16,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 8,
    },
    strategyDesc: {
      fontSize: 14,
      color: colors.textSecondary,
      lineHeight: 20,
    },
    mistakesList: {
      gap: 12,
    },
    mistakeItem: {
      backgroundColor: '#FEF2F2',
      padding: 16,
      borderRadius: 8,
      borderLeftWidth: 4,
      borderLeftColor: colors.error,
    },
    mistakeTitle: {
      fontSize: 16,
      fontWeight: 'bold',
      color: colors.error,
      marginBottom: 8,
    },
    mistakeDesc: {
      fontSize: 14,
      color: colors.textSecondary,
      lineHeight: 20,
    },
  });

  return (
    <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
      {renderHeader()}
      
      <View style={styles.content}>
        <View style={styles.leftPanel}>
          {renderCalculator()}
        </View>
        
        <View style={styles.rightPanel}>
          {renderDonutChart()}
          {renderResults()}
        </View>
      </View>
      
      <View style={styles.fullWidthSection}>
        {renderAmortizationSchedule()}
        {renderBusinessLoanInformation()}
        {renderSEOContent()}
        {renderFAQ()}
        <Footer />
      </View>
    </ScrollView>
  );
};

export default BusinessLoanEMICalculatorScreen;